import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import type { ReactNode } from 'react'
import { creditApi } from '@/services/api'
import { useAuth } from '@/contexts/AuthContext'

interface CreditData {
  total_balance: number
  allocation_balance: number
  topup_balance: number
  current_tier: string
  allocation_expires_at: string | null
}

/**
 * Per-feature credit costs as defined in config/ai.php on the backend.
 * Keys match the feature slugs used in credit_costs, e.g. 'content_optimizer', 'daily_ideas'.
 * Values are credit integers (1 credit = $0.01).
 */
export type FeatureCreditCosts = Record<string, number>

interface CreditContextType {
  creditData: CreditData | null
  /** Live per-feature credit costs fetched from the backend config. */
  creditCosts: FeatureCreditCosts
  isLoading: boolean
  error: string | null
  refreshCredits: () => Promise<void>
}

const CreditContext = createContext<CreditContextType | undefined>(undefined)

export const useCredits = () => {
  const context = useContext(CreditContext)
  if (context === undefined) {
    throw new Error('useCredits must be used within a CreditProvider')
  }
  return context
}

export const CreditProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth()
  const [creditData, setCreditData] = useState<CreditData | null>(null)
  const [creditCosts, setCreditCosts] = useState<FeatureCreditCosts>({})
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const refreshCredits = useCallback(async () => {
    if (!user) return

    setIsLoading(true)
    setError(null)

    try {
      // Fetch balance and feature costs in parallel
      const [balanceRes, costsRes] = await Promise.allSettled([
        creditApi.getBalance(),
        creditApi.getCosts(),
      ])

      if (balanceRes.status === 'fulfilled') {
        const response = balanceRes.value
        if ((response.success || response.status === 'success') && response.data) {
          setCreditData(response.data)
        } else {
          setError('Failed to load credit balance')
        }
      } else {
        setError('An error occurred while fetching credit balance')
        console.error('Error fetching credits:', balanceRes.reason)
      }

      if (costsRes.status === 'fulfilled') {
        setCreditCosts(costsRes.value)
      } else {
        // Non-fatal — button labels will fall back to a dash if costs can't load
        console.warn('Could not fetch AI credit costs:', costsRes.reason)
      }
    } finally {
      setIsLoading(false)
    }
  }, [user])

  // Fetch initial credits when user logs in
  useEffect(() => {
    if (user) {
      refreshCredits()
    } else {
      setCreditData(null)
      setCreditCosts({})
    }
  }, [user, refreshCredits])

  // Listen for global credit deduction events for real-time balance updates
  useEffect(() => {
    const handleCreditUpdate = () => {
      refreshCredits()
    }

    window.addEventListener('credit-updated', handleCreditUpdate)
    return () => {
      window.removeEventListener('credit-updated', handleCreditUpdate)
    }
  }, [refreshCredits])

  return (
    <CreditContext.Provider value={{ creditData, creditCosts, isLoading, error, refreshCredits }}>
      {children}
    </CreditContext.Provider>
  )
}
